import 'react-image-crop/dist/ReactCrop.css'
import alert from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Button from '@jetbrains/ring-ui/components/button/button'
import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {Content, Header} from '@jetbrains/ring-ui/components/island/island'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import type {SyntheticEvent} from 'react'
import {useCallback, useRef, useState} from 'react'
import type {Crop} from 'react-image-crop'
import ReactCrop, {makeAspectCrop} from 'react-image-crop'

import {restApi} from '../../../services/rest'
import type {UserId} from '../../../types'

import styles from './AvatarEditor.css'

type Props = {
  userId: UserId
  src: string
  onCloseAttempt?: () => void
  onError?: (e: SyntheticEvent<HTMLImageElement>) => void
}

const LIMIT = 1e7

export default function AvatarEditorDialog({src, onCloseAttempt, userId, onError}: Props) {
  const [crop, setCrop] = useState<Crop>()
  const [currentCrop, setCurrentCrop] = useState<Crop>()
  const [saving, setSaving] = useState(false)
  const imgRef = useRef<HTMLImageElement>()
  const onLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget
    setCrop(
      makeAspectCrop(
        {
          unit: '%',
          height: 100,
        },
        1,
        e.currentTarget.width,
        e.currentTarget.height,
      ),
    )
  }, [])
  const [saveAvatar] = restApi.endpoints.putAvatar.useMutation()
  const onSave = () => {
    const image = imgRef.current
    if (image == null || currentCrop == null) {
      alert.error('No image selected')
      return
    }
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx == null) {
      return
    }
    setSaving(true)

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    ctx.imageSmoothingQuality = 'high'

    const draw = (factor = 1) => {
      const pixelRatio = factor * window.devicePixelRatio

      canvas.width = currentCrop.width * pixelRatio * scaleX
      canvas.height = currentCrop.height * pixelRatio * scaleY

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

      ctx.drawImage(
        image,
        currentCrop.x * scaleX,
        currentCrop.y * scaleY,
        currentCrop.width * scaleX,
        currentCrop.height * scaleY,
        0,
        0,
        currentCrop.width * scaleX,
        currentCrop.height * scaleY,
      )

      canvas.toBlob(async blob => {
        if (blob != null) {
          if (blob.size > LIMIT) {
            draw(factor / 2)
          } else {
            try {
              await saveAvatar({userLocator: `id:${userId}`, body: {avatar: blob}})
              onCloseAttempt?.()
            } finally {
              setSaving(false)
            }
          }
        } else {
          alert.error('No image selected')
          setSaving(false)
        }
      })
    }
    draw()
  }
  return (
    <Dialog contentClassName={styles.dialog} show={src != null} onCloseAttempt={onCloseAttempt}>
      <Header>{'Crop Avatar'}</Header>
      <Content>
        <ReactCrop
          crop={crop}
          aspect={1}
          onChange={setCrop}
          onComplete={setCurrentCrop}
          keepSelection
        >
          <img
            alt="avatar"
            src={src}
            style={{maxHeight: 'calc(100vh - 208px)'}}
            onLoad={onLoad}
            onError={onError}
          />
        </ReactCrop>
      </Content>
      <Panel>
        <Button
          onClick={onSave}
          primary
          loader={saving}
          disabled={currentCrop == null || currentCrop.width === 0}
        >
          {'Save avatar'}
        </Button>
        <Button onClick={onCloseAttempt}>{'Cancel'}</Button>
      </Panel>
    </Dialog>
  )
}
